import { useQuery } from '@tanstack/react-query';
import { isToday, parseISO } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import { useState, useMemo, useEffect } from 'react';
import { R } from '@/features/booking/resources/Texts.resx';
import { ConsultationTypeEnum } from '@/shared/enums';
import { ITimeslotsByDay } from '@/shared/interfaces';
import { Utils } from '@/shared/Utils';
import {
    bookingDraftAtom,
    preferredCaregiverUidAtom,
    selectedTimeSlotDateAtom,
    visibleDayAtom,
} from '@/features/booking/shared/model/bookingState';
import { fetchSchedule } from '@/features/booking/widgets/book-or-queue-up/api/fetchSchedule';

type useTimeslotsParams = {
    consultationType?: ConsultationTypeEnum;
    performInitialFetch?: boolean;
};

export const useTimeslots = ({
    consultationType,
    performInitialFetch = true,
}: useTimeslotsParams) => {
    const preferredCaregiverUid = useAtomValue(preferredCaregiverUidAtom);
    const hasPreferredCaregiver = !!preferredCaregiverUid;

    const draftState = useAtomValue(bookingDraftAtom);
    const [selectedTimeSlot, setSelectedTimeSlot] = useAtom(
        selectedTimeSlotDateAtom,
    );

    const {
        data: allTimeSlots = [],
        refetch,
        isFetching,
        error,
    } = useQuery({
        queryKey: ['schedule', consultationType],
        queryFn: () =>
            fetchSchedule(consultationType, selectedTimeSlot?.toISOString()),
        enabled: !!consultationType && !!draftState && performInitialFetch,
        staleTime: 1000 * 60 * 2, // 2 min
    });

    const hasAvailableTimeslots = allTimeSlots.length > 0;

    const timeslotsByDay = useMemo<ITimeslotsByDay[]>(() => {
        const grouped: Record<string, [Date, string[]][]> = {};

        for (const slot of allTimeSlots) {
            const date = parseISO(slot.Start);
            const day = isToday(date) ? R.Today : Utils.formatLongDate(date);

            grouped[day] ??= [];
            grouped[day].push([date, slot.AvailableCaregivers]);
        }

        return Object.entries(grouped).map(([day, data]) => ({
            day,
            timeslots: hasPreferredCaregiver
                ? data
                      .filter((x) => x[1].includes(preferredCaregiverUid))
                      .map((x) => x[0])
                : data.map((x) => x[0]),
            availableCaregivers: data.flatMap((x) => x[1]),
        }));
    }, [allTimeSlots, preferredCaregiverUid, hasPreferredCaregiver]);

    const [visibleDay, setVisibleDay] = useAtom(visibleDayAtom);
    const currentTimeslotDay = useMemo(() => {
        return visibleDay == null
            ? null
            : timeslotsByDay.findIndex((x) => x.day === visibleDay);
    }, [visibleDay, timeslotsByDay]);
    const visibleTimeslotsPerDay =
        currentTimeslotDay == null ? null : timeslotsByDay[currentTimeslotDay];

    // Set initial visible date to the first available for the selected caregiver.
    // It stores it immediately to keep it stable between caregiver changes.
    useEffect(() => {
        if (!timeslotsByDay.length) return;
        if (currentTimeslotDay != null) return;

        if (preferredCaregiverUid) {
            for (const day of timeslotsByDay) {
                if (day.availableCaregivers.includes(preferredCaregiverUid)) {
                    setVisibleDay(day.day);
                    return;
                }
            }
        }

        setVisibleDay(timeslotsByDay[0].day);
    }, [
        timeslotsByDay,
        currentTimeslotDay,
        preferredCaregiverUid,
        setVisibleDay,
    ]);

    // set initial timeslot day to the one containing selected timeslot (on refresh)
    const [hasSetInitialDay, setHasSetInitialDay] = useState(true);
    useEffect(() => {
        if (draftState === null) setHasSetInitialDay(false); // draft state is only null in this step on refresh

        if (!selectedTimeSlot || hasSetInitialDay || !timeslotsByDay.length) {
            return;
        }

        const day = timeslotsByDay.findIndex((x) => {
            const sample = new Date(x.timeslots[0]);
            const sameDate = sample.getDate() === selectedTimeSlot.getDate();
            const sameMonth = sample.getMonth() === selectedTimeSlot.getMonth();
            return sameDate && sameMonth;
        });

        setVisibleDay(timeslotsByDay[day === -1 ? 0 : day]?.day);
        setHasSetInitialDay(true);
    }, [selectedTimeSlot, timeslotsByDay, hasSetInitialDay]);

    function findNextIndex(direction: -1 | 1): number | null {
        if (!hasPreferredCaregiver) {
            const nextIndex = currentTimeslotDay + direction;
            // We test the boundaries of the array
            return timeslotsByDay[nextIndex] ? nextIndex : null;
        }

        for (
            let nextIndex = currentTimeslotDay + direction;
            timeslotsByDay[nextIndex];
            nextIndex += direction
        ) {
            const slot = timeslotsByDay[nextIndex];
            if (slot.availableCaregivers.includes(preferredCaregiverUid)) {
                return nextIndex;
            }
        }

        return null;
    }
    function selectNextDay() {
        const next = findNextIndex(1);
        if (next != null) {
            setVisibleDay(timeslotsByDay[next]?.day);
        }
    }
    function selectPreviousDay() {
        const next = findNextIndex(-1);
        if (next != null) {
            setVisibleDay(timeslotsByDay[next]?.day);
        }
    }

    return {
        hasAvailableTimeslots,
        visibleTimeslotsPerDay,

        selectedTimeSlot,
        setSelectedTimeSlot,

        selectNextDay: findNextIndex(1) === null ? null : selectNextDay,
        selectPreviousDay:
            findNextIndex(-1) === null ? null : selectPreviousDay,

        refetch,
        isFetching,
        error,
    };
};
